import type Phaser from 'phaser';
import { loadPrefs } from './prefs.js';

export type FeelPreset =
  | 'whisper'      // micro tactile (chip place, dice/card land, can't-afford)
  | 'win-subtle'   // routine win — mid-round, low-multiplier
  | 'win-heavy'    // climactic win — point made, ≥1.5x multiplier
  | 'lose-medium'  // routine loss — single-bet loss
  | 'lose-heavy';  // climactic loss — seven-out

interface PresetSpec {
  duration: number;        // ms — shake duration
  cameraIntensity: number; // Phaser viewport-fraction per frame (0..1)
  flinchPx: number;        // peak transform offset for object-local flinch
  haptic: number[];        // navigator.vibrate pattern
  flash?: { r: number; g: number; b: number; duration?: number }; // optional color flash; duration defaults to shake duration
}

const PRESETS: Record<FeelPreset, PresetSpec> = {
  'whisper':     { duration:  80, cameraIntensity: 0.002, flinchPx: 1.5, haptic: [10] },
  'win-subtle':  { duration: 200, cameraIntensity: 0.008, flinchPx: 4,   haptic: [20], flash: { r: 255, g: 215, b:  90, duration: 160 } },
  'win-heavy':   { duration: 400, cameraIntensity: 0.016, flinchPx: 7,   haptic: [40, 40, 40], flash: { r: 255, g: 200, b:  60, duration: 500 } },
  'lose-medium': { duration: 280, cameraIntensity: 0.010, flinchPx: 5,   haptic: [40], flash: { r: 220, g:  60, b:  60 } },
  'lose-heavy':  { duration: 400, cameraIntensity: 0.016, flinchPx: 7,   haptic: [80], flash: { r: 220, g:  40, b:  40, duration: 500 } },
};

/** Wins at or above this multiplier are climactic regardless of round state. */
const CELEBRATE_MULTIPLIER_THRESHOLD = 1.5;

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function shakeEnabled(): boolean {
  const pref = loadPrefs().screenShake ?? 'auto';
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  return !prefersReducedMotion();
}

export function hapticsEnabled(): boolean {
  const pref = loadPrefs().haptics ?? 'auto';
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

type FlinchTarget = Phaser.GameObjects.GameObject & { x: number; y: number };

export function shake(scene: Phaser.Scene, preset: FeelPreset): void {
  const spec = PRESETS[preset];
  if (shakeEnabled()) {
    const cam = scene.cameras.main;
    cam.shake(spec.duration, spec.cameraIntensity);
    if (spec.flash) {
      cam.flash(spec.flash.duration ?? spec.duration, spec.flash.r, spec.flash.g, spec.flash.b);
    }
  }
  if (hapticsEnabled() && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(spec.haptic);
  }
}

export function flinch(target: FlinchTarget, preset: FeelPreset): void {
  if (!shakeEnabled() && !hapticsEnabled()) return;

  const spec = PRESETS[preset];
  if (shakeEnabled()) {
    const originX = target.x;
    const originY = target.y;
    const peak = spec.flinchPx;
    // Three damped offset keyframes returning to origin. Random direction
    // per keyframe — feels like a real flinch, not a sine wave.
    const dx1 = (Math.random() * 2 - 1) * peak;
    const dy1 = (Math.random() * 2 - 1) * peak;
    const dx2 = (Math.random() * 2 - 1) * (peak * 0.6);
    const dy2 = (Math.random() * 2 - 1) * (peak * 0.6);
    target.scene.tweens.add({
      targets: target,
      x: [originX + dx1, originX + dx2, originX],
      y: [originY + dy1, originY + dy2, originY],
      duration: spec.duration,
      ease: 'Sine.easeOut',
    });
  }
  if (hapticsEnabled() && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(spec.haptic);
  }
}

export interface ResolutionParams {
  /** Player's net cents on this resolution (positive = win, negative = loss). */
  netDelta: number;
  /** Largest payout multiplier hit on this resolution (1, 1.5, 7, 15, etc.). */
  maxMultiplier: number;
  /** True when this resolution ends the round (e.g. seven-out). */
  isRoundEnd: boolean;
}

/**
 * Haptic pulse for a die hitting a wall. Intensity scales with impact speed
 * (px/s). Skipped below ~200 px/s so settle-jitter wall touches don't buzz.
 */
export function wallImpactHaptic(speed: number): void {
  if (!hapticsEnabled()) return;
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  if (speed < 200) return;
  const ms = speed >= 1500 ? 30 : speed >= 600 ? 15 : 8;
  navigator.vibrate(ms);
}

export function fromResolution(scene: Phaser.Scene, params: ResolutionParams): void {
  const { netDelta, maxMultiplier, isRoundEnd } = params;
  if (netDelta === 0) return;
  if (netDelta > 0) {
    const climactic = maxMultiplier >= CELEBRATE_MULTIPLIER_THRESHOLD || isRoundEnd;
    shake(scene, climactic ? 'win-heavy' : 'win-subtle');
  } else {
    shake(scene, isRoundEnd ? 'lose-heavy' : 'lose-medium');
  }
}
